import * as React from "react";
import { HoloBadge, HoloShimmer } from "@/components/Holo";
import { Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { AspectRatio, DesignPackageItem } from "@/services/bedrock/dtos/DesignPackage";
import { SlideState } from "../slideState";
import { DownloadFab } from "./ImageDownload";
import { ASPECT_CLASS } from "./PackageCard";

interface SlidePreviewProps {
  item: DesignPackageItem;
  aspectRatio: AspectRatio;
  imageUrl: string | null;
  imageState: "ready" | "updating" | "missing";
  state: SlideState;
  selected?: boolean;
  onClick?: () => void;
  size: "thumb" | "large";
  /** What the large view says while its image is on the way, such as the theme it's rendering with. */
  loadingLabel?: string;
  /** The file name to save the image under; shows a download button on hover once the image is ready. */
  downloadName?: string;
  className?: string;
}

const StatusMark: React.FC<React.PropsWithChildren<{ label: string; "data-testid": string }>> = ({
  label,
  children,
  "data-testid": testId,
}) => (
  <Tooltip>
    <Tooltip.Trigger
      render={<span className="absolute top-1.5 right-1.5" role="img" aria-label={label} data-testid={testId} />}
    >
      {children}
    </Tooltip.Trigger>
    <Tooltip.Content>{label}</Tooltip.Content>
  </Tooltip>
);

/** One slide's image for the selected theme, with the working and new-version treatments. */
export const SlidePreview: React.FC<SlidePreviewProps> = ({
  item,
  aspectRatio,
  imageUrl,
  imageState,
  state,
  selected,
  onClick,
  size,
  loadingLabel,
  downloadName,
  className: extraClassName,
}) => {
  const shimmer = state === "candidate_rendering" || imageState === "missing" || !imageUrl;
  const body = shimmer ? (
    <HoloShimmer
      aspectRatio={aspectRatio}
      showLogo={size === "large"}
      label={
        size !== "large"
          ? undefined
          : state === "candidate_rendering"
          ? item.progressStatus ?? "Working…"
          : loadingLabel
      }
      className="rounded-none"
    />
  ) : (
    <div className={cn("relative size-full", ASPECT_CLASS[aspectRatio])}>
      <img src={imageUrl} alt={`Slide ${item.position}`} className="size-full object-cover" />
      {imageState === "updating" && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
          <span className="rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm">
            {loadingLabel ?? "Updating…"}
          </span>
        </div>
      )}
    </div>
  );

  const className = cn(
    "group/slide relative shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100",
    size === "thumb" ? "w-28 snap-start" : "w-full",
    selected && "ring-2 ring-primary",
    (state === "approving" || state === "discarding") && "opacity-60",
    extraClassName
  );

  const content = (
    <>
      {body}
      {downloadName && !shimmer && imageState === "ready" && (
        <DownloadFab
          url={imageUrl}
          name={downloadName}
          // Out of the way until the slide is hovered; always there where there's no hover.
          className="absolute right-3 bottom-3 opacity-0 transition-opacity group-hover/slide:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
        />
      )}
      {state === "candidate_ready" && size === "large" && (
        <HoloBadge label="New version" className="absolute top-1.5 right-1.5" size="sm" />
      )}
      {state === "candidate_failed" && size === "thumb" && (
        <StatusMark label="Couldn't update" data-testid="thumb-failed">
          <span className="block size-2.5 rounded-full bg-destructive ring-2 ring-white" />
        </StatusMark>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={cn(className, "cursor-pointer text-left")}
        onClick={onClick}
        aria-pressed={selected}
        aria-label={`Slide ${item.position}`}
        data-testid="slide-preview"
      >
        {content}
      </button>
    );
  }
  return (
    <div className={className} data-testid="slide-preview">
      {content}
    </div>
  );
};
